// =====================================================
// FORMULARIO DE CARGA - SOLICITUD DE CRÉDITO AGROPECUARIO
// src/pages/Agro/Evaluacion/FormularioCarga.js
// =====================================================
// Wizard de 4 pasos - v2 con estado persistente
// =====================================================

import React, { useState } from "react"
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Button,
  Progress,
  Nav,
  NavItem,
  NavLink,
  Alert,
  FormGroup,
  Label,
  Input,
  Table,
  Badge,
  Spinner,
  InputGroup,
} from "reactstrap"
import { Formik, Form, Field, FieldArray, ErrorMessage } from "formik"
import * as Yup from "yup"
import classnames from "classnames"
import { useHistory } from "react-router-dom"

import { validarCuit, crearEvaluacion } from "api/agro"
import Layout from "components/Layout"
import toastHandler from "toastHandler"

// =====================================================
// ESQUEMAS DE VALIDACIÓN POR PASO
// =====================================================

const PasoProductorSchema = Yup.object().shape({
  cuit: Yup.string()
    .matches(/^\d{11}$/, "CUIT debe tener 11 dígitos")
    .required("CUIT requerido"),
  razonSocial: Yup.string().required("Razón social requerida"),
})

const PasoEstablecimientosSchema = Yup.object().shape({
  establecimientos: Yup.array()
    .of(
      Yup.object().shape({
        nombre: Yup.string().required("Nombre requerido"),
        superficieTotal: Yup.number()
          .min(1, "Mínimo 1 ha")
          .required("Superficie requerida"),
      }),
    )
    .min(1, "Debe agregar al menos un establecimiento"),
})

const PasoCreditoSchema = Yup.object().shape({
  credito: Yup.object().shape({
    monto: Yup.number().min(1, "Monto requerido").required("Monto requerido"),
    destino: Yup.string().required("Seleccione destino"),
    plazoMeses: Yup.string().required("Plazo requerido"),
    garantia: Yup.string().required("Seleccione garantía"),
  }),
})

const getValidationSchema = paso => {
  switch (paso) {
    case 1:
      return PasoProductorSchema
    case 2:
      return PasoEstablecimientosSchema
    case 4:
      return PasoCreditoSchema
    default:
      return Yup.object()
  }
}

// =====================================================
// VALORES INICIALES
// =====================================================

const getInitialValues = () => ({
  cuit: "",
  razonSocial: "",
  email: "",
  telefono: "",
  establecimientos: [
    {
      nombre: "",
      provincia: "",
      departamento: "",
      superficieTotal: "",
      superficiePropia: "",
      superficieArrendada: "",
    },
  ],
  cultivos: [],
  stockGanadero: {
    vacas: "",
    vaquillonas: "",
    terneros: "",
    novillos: "",
    toros: "",
  },
  credito: {
    monto: "",
    destino: "",
    plazoMeses: "",
    garantia: "",
  },
})

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

const FormularioCarga = () => {
  const history = useHistory()
  const { setError, setSuccess } = toastHandler()

  const [pasoActual, setPasoActual] = useState(1)
  const [validacionAfip, setValidacionAfip] = useState(null)
  const [validandoAfip, setValidandoAfip] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // ⭐ CLAVE: Estado global que persiste todos los datos del formulario
  const [formData, setFormData] = useState(getInitialValues())

  // Validar CUIT en AFIP
  const handleValidarCuit = async (cuit, setFieldValue) => {
    if (!cuit || cuit.length !== 11) return

    setValidandoAfip(true)
    try {
      const resultado = await validarCuit(cuit)
      setValidacionAfip(resultado)

      if (resultado.valido && resultado.razonSocial) {
        setFieldValue("razonSocial", resultado.razonSocial)
      }
    } catch (error) {
      console.error("Error validando CUIT:", error)
      setValidacionAfip({
        valido: false,
        mensaje: "Error de conexión con AFIP",
      })
    } finally {
      setValidandoAfip(false)
    }
  }

  // ⭐ Guardar datos del paso actual y avanzar
  const handleSiguiente = async (values, validateForm, setTouched) => {
    const errors = await validateForm()

    if (Object.keys(errors).length > 0) {
      // Marcar todos los campos como touched para mostrar errores
      const touchedFields = {}
      Object.keys(errors).forEach(key => {
        touchedFields[key] = true
      })
      setTouched(touchedFields)
      return
    }

    // ⭐ Guardar valores en el estado global
    setFormData(prev => ({ ...prev, ...values }))
    setPasoActual(p => Math.min(p + 1, 4))
  }

  // ⭐ Guardar datos y retroceder
  const handleAnterior = values => {
    setFormData(prev => ({ ...prev, ...values }))
    setPasoActual(p => Math.max(p - 1, 1))
  }

  // Enviar formulario completo
  const handleSubmit = async values => {
    // ⭐ Combinar estado global con valores actuales
    const datosCompletos = { ...formData, ...values }

    console.log("=== PAYLOAD COMPLETO ===")
    console.log(JSON.stringify(datosCompletos, null, 2))

    setSubmitting(true)
    try {
      const resultado = await crearEvaluacion(datosCompletos)
      setSuccess("Solicitud creada exitosamente")
      history.push(`/agro/evaluaciones/${resultado.idEvaluacion}`)
    } catch (error) {
      console.error("Error creando evaluación:", error)
      setError(error)
    } finally {
      setSubmitting(false)
    }
  }

  // =====================================================
  // RENDER DE PASOS
  // =====================================================

  const renderPaso = (values, setFieldValue) => {
    switch (pasoActual) {
      case 1:
        return (
          <>
            <h5 className="mb-4 text-primary">
              <i className="mdi mdi-account-circle me-2" />
              Paso 1: Datos del Productor
            </h5>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>
                    CUIT <span className="text-danger">*</span>
                  </Label>
                  <InputGroup>
                    <Field
                      as={Input}
                      type="text"
                      name="cuit"
                      placeholder="Ej: 20123456789"
                      maxLength={11}
                    />
                    <Button
                      color="outline-secondary"
                      onClick={() =>
                        handleValidarCuit(values.cuit, setFieldValue)
                      }
                      disabled={
                        validandoAfip ||
                        !values.cuit ||
                        values.cuit.length !== 11
                      }
                    >
                      {validandoAfip ? <Spinner size="sm" /> : "Validar AFIP"}
                    </Button>
                  </InputGroup>
                  <ErrorMessage
                    name="cuit"
                    component="div"
                    className="text-danger small mt-1"
                  />
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label>
                    Razón Social <span className="text-danger">*</span>
                  </Label>
                  <Field
                    as={Input}
                    type="text"
                    name="razonSocial"
                    placeholder="Nombre o razón social"
                  />
                  <ErrorMessage
                    name="razonSocial"
                    component="div"
                    className="text-danger small mt-1"
                  />
                </FormGroup>
              </Col>
            </Row>

            {validacionAfip && (
              <Alert
                color={validacionAfip.valido ? "success" : "warning"}
                className="mt-2"
              >
                <i
                  className={`mdi ${validacionAfip.valido ? "mdi-check-circle" : "mdi-alert"} me-2`}
                />
                {validacionAfip.mensaje ||
                  (validacionAfip.valido
                    ? "CUIT válido en AFIP"
                    : "No se pudo validar")}
              </Alert>
            )}

            <Row className="mt-3">
              <Col md={6}>
                <FormGroup>
                  <Label>Email</Label>
                  <Field
                    as={Input}
                    type="email"
                    name="email"
                    placeholder="email@ejemplo.com"
                  />
                  <ErrorMessage
                    name="email"
                    component="div"
                    className="text-danger small mt-1"
                  />
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label>Teléfono</Label>
                  <Field
                    as={Input}
                    type="text"
                    name="telefono"
                    placeholder="Ej: 11-1234-5678"
                  />
                </FormGroup>
              </Col>
            </Row>
          </>
        )

      case 2:
        return (
          <>
            <h5 className="mb-4 text-primary">
              <i className="mdi mdi-barn me-2" />
              Paso 2: Establecimientos Agropecuarios
            </h5>

            <FieldArray name="establecimientos">
              {({ push, remove }) => (
                <>
                  {values.establecimientos.map((est, index) => (
                    <Card key={index} className="mb-3 border shadow-sm">
                      <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="mb-0 text-secondary">
                            <i className="mdi mdi-map-marker me-1" />
                            Establecimiento #{index + 1}
                          </h6>
                          {values.establecimientos.length > 1 && (
                            <Button
                              color="danger"
                              size="sm"
                              outline
                              onClick={() => remove(index)}
                            >
                              <i className="mdi mdi-delete" />
                            </Button>
                          )}
                        </div>

                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Label>
                                Nombre <span className="text-danger">*</span>
                              </Label>
                              <Field
                                as={Input}
                                name={`establecimientos.${index}.nombre`}
                                placeholder="Nombre del campo"
                              />
                              <ErrorMessage
                                name={`establecimientos.${index}.nombre`}
                                component="div"
                                className="text-danger small"
                              />
                            </FormGroup>
                          </Col>
                          {/* BDSJEA-134: el RENSPA se saca del alta de
                              establecimientos. El banco no lo usa y no siempre
                              lo tiene disponible al momento de cargar. */}
                        </Row>

                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Label>Provincia</Label>
                              <Field
                                as={Input}
                                type="select"
                                name={`establecimientos.${index}.provincia`}
                              >
                                <option value="">Seleccione...</option>
                                <option value="BUENOS_AIRES">
                                  Buenos Aires
                                </option>
                                <option value="CORDOBA">Córdoba</option>
                                <option value="SANTA_FE">Santa Fe</option>
                                <option value="ENTRE_RIOS">Entre Ríos</option>
                                <option value="LA_PAMPA">La Pampa</option>
                                <option value="CHACO">Chaco</option>
                                <option value="SANTIAGO_DEL_ESTERO">
                                  Santiago del Estero
                                </option>
                                <option value="SALTA">Salta</option>
                              </Field>
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label>Departamento/Partido</Label>
                              <Field
                                as={Input}
                                name={`establecimientos.${index}.departamento`}
                                placeholder="Ej: Pergamino"
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={4}>
                            <FormGroup>
                              <Label>
                                Superficie Total (ha){" "}
                                <span className="text-danger">*</span>
                              </Label>
                              <Field
                                as={Input}
                                type="number"
                                name={`establecimientos.${index}.superficieTotal`}
                                placeholder="0"
                                min="0"
                              />
                              <ErrorMessage
                                name={`establecimientos.${index}.superficieTotal`}
                                component="div"
                                className="text-danger small"
                              />
                            </FormGroup>
                          </Col>
                          <Col md={4}>
                            <FormGroup>
                              <Label>Superficie Propia (ha)</Label>
                              <Field
                                as={Input}
                                type="number"
                                name={`establecimientos.${index}.superficiePropia`}
                                placeholder="0"
                                min="0"
                              />
                            </FormGroup>
                          </Col>
                          <Col md={4}>
                            <FormGroup>
                              <Label>Superficie Arrendada (ha)</Label>
                              <Field
                                as={Input}
                                type="number"
                                name={`establecimientos.${index}.superficieArrendada`}
                                placeholder="0"
                                min="0"
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  ))}

                  <Button
                    color="success"
                    outline
                    onClick={() =>
                      push({
                        nombre: "",
                        provincia: "",
                        departamento: "",
                        superficieTotal: "",
                        superficiePropia: "",
                        superficieArrendada: "",
                      })
                    }
                  >
                    <i className="mdi mdi-plus me-1" />
                    Agregar Establecimiento
                  </Button>
                </>
              )}
            </FieldArray>
          </>
        )

      case 3:
        return (
          <>
            <h5 className="mb-4 text-primary">
              <i className="mdi mdi-sprout me-2" />
              Paso 3: Actividad Productiva
            </h5>

            {/* Cultivos */}
            <Card className="mb-4 border">
              <CardHeader className="bg-light border-bottom">
                <h6 className="mb-0">
                  <i className="mdi mdi-corn me-2 text-warning" />
                  Cultivos (Opcional)
                </h6>
              </CardHeader>
              <CardBody>
                <FieldArray name="cultivos">
                  {({ push, remove }) => (
                    <>
                      {values.cultivos.length === 0 ? (
                        <p className="text-muted mb-2">
                          No hay cultivos cargados
                        </p>
                      ) : (
                        values.cultivos.map((cultivo, index) => (
                          <Row
                            key={index}
                            className="align-items-end mb-2 pb-2 border-bottom"
                          >
                            <Col md={3}>
                              <FormGroup className="mb-0">
                                <Label className="small">Cultivo</Label>
                                <Field
                                  as={Input}
                                  type="select"
                                  name={`cultivos.${index}.tipoCultivo`}
                                  bsSize="sm"
                                >
                                  <option value="">Seleccione...</option>
                                  <option value="SOJA">Soja</option>
                                  <option value="MAIZ">Maíz</option>
                                  <option value="TRIGO">Trigo</option>
                                  <option value="GIRASOL">Girasol</option>
                                  <option value="CEBADA">Cebada</option>
                                </Field>
                              </FormGroup>
                            </Col>
                            <Col md={2}>
                              <FormGroup className="mb-0">
                                <Label className="small">Hectáreas</Label>
                                <Field
                                  as={Input}
                                  type="number"
                                  name={`cultivos.${index}.hectareas`}
                                  bsSize="sm"
                                  min="0"
                                />
                              </FormGroup>
                            </Col>
                            <Col md={2}>
                              <FormGroup className="mb-0">
                                <Label className="small">Rinde (qq/ha)</Label>
                                <Field
                                  as={Input}
                                  type="number"
                                  name={`cultivos.${index}.rindeEstimado`}
                                  bsSize="sm"
                                  min="0"
                                />
                              </FormGroup>
                            </Col>
                            <Col md={2}>
                              <FormGroup className="mb-0">
                                <Label className="small">Campaña</Label>
                                <Field
                                  as={Input}
                                  name={`cultivos.${index}.campana`}
                                  bsSize="sm"
                                  placeholder="24/25"
                                />
                              </FormGroup>
                            </Col>
                            <Col md={2}>
                              <FormGroup className="mb-0 d-flex align-items-center pt-3">
                                <Field
                                  type="checkbox"
                                  name={`cultivos.${index}.tieneSeguro`}
                                  className="me-2"
                                />
                                <Label className="small mb-0">Asegurado</Label>
                              </FormGroup>
                            </Col>
                            <Col md={1}>
                              <Button
                                color="danger"
                                size="sm"
                                outline
                                onClick={() => remove(index)}
                              >
                                <i className="mdi mdi-delete" />
                              </Button>
                            </Col>
                          </Row>
                        ))
                      )}

                      <Button
                        color="success"
                        size="sm"
                        outline
                        className="mt-2"
                        onClick={() =>
                          push({
                            tipoCultivo: "",
                            hectareas: "",
                            rindeEstimado: "",
                            campana: "",
                            tieneSeguro: false,
                          })
                        }
                      >
                        <i className="mdi mdi-plus me-1" /> Agregar Cultivo
                      </Button>
                    </>
                  )}
                </FieldArray>
              </CardBody>
            </Card>

            {/* Stock Ganadero */}
            <Card className="border">
              <CardHeader className="bg-light border-bottom">
                <h6 className="mb-0">
                  <i className="mdi mdi-cow me-2 text-success" />
                  Stock Ganadero - Declaración Jurada (Opcional)
                </h6>
              </CardHeader>
              <CardBody>
                <Row>
                  {[
                    { name: "vacas", label: "Vacas" },
                    { name: "vaquillonas", label: "Vaquillonas" },
                    { name: "terneros", label: "Terneros/as" },
                    { name: "novillos", label: "Novillos" },
                    { name: "toros", label: "Toros" },
                  ].map(({ name, label }) => (
                    <Col md={2} key={name}>
                      <FormGroup>
                        <Label className="small">{label}</Label>
                        <Field
                          as={Input}
                          type="number"
                          name={`stockGanadero.${name}`}
                          placeholder="0"
                          min="0"
                        />
                      </FormGroup>
                    </Col>
                  ))}
                  <Col md={2} className="d-flex align-items-end pb-3">
                    <Badge color="info" className="p-2">
                      Total:{" "}
                      {(parseInt(values.stockGanadero?.vacas) || 0) +
                        (parseInt(values.stockGanadero?.vaquillonas) || 0) +
                        (parseInt(values.stockGanadero?.terneros) || 0) +
                        (parseInt(values.stockGanadero?.novillos) || 0) +
                        (parseInt(values.stockGanadero?.toros) || 0)}{" "}
                      cab.
                    </Badge>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </>
        )

      case 4:
        return (
          <>
            <h5 className="mb-4 text-primary">
              <i className="mdi mdi-cash-multiple me-2" />
              Paso 4: Solicitud de Crédito
            </h5>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>
                    Monto Solicitado ($) <span className="text-danger">*</span>
                  </Label>
                  <Field
                    as={Input}
                    type="number"
                    name="credito.monto"
                    placeholder="0"
                    min="0"
                  />
                  <ErrorMessage
                    name="credito.monto"
                    component="div"
                    className="text-danger small mt-1"
                  />
                  {values.credito?.monto && (
                    <small className="text-success">
                      {new Intl.NumberFormat("es-AR", {
                        style: "currency",
                        currency: "ARS",
                      }).format(values.credito.monto)}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label>
                    Destino del Crédito <span className="text-danger">*</span>
                  </Label>
                  <Field as={Input} type="select" name="credito.destino">
                    <option value="">Seleccione...</option>
                    <option value="CAPITAL_TRABAJO">Capital de Trabajo</option>
                    <option value="INVERSION">Inversión</option>
                    <option value="COMPRA_INSUMOS">Compra de Insumos</option>
                    <option value="COMPRA_MAQUINARIA">
                      Compra de Maquinaria
                    </option>
                    <option value="COMPRA_HACIENDA">Compra de Hacienda</option>
                    <option value="REFINANCIACION">Refinanciación</option>
                  </Field>
                  <ErrorMessage
                    name="credito.destino"
                    component="div"
                    className="text-danger small mt-1"
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>
                    Plazo (meses) <span className="text-danger">*</span>
                  </Label>
                  <Field as={Input} type="select" name="credito.plazoMeses">
                    <option value="">Seleccione...</option>
                    <option value="6">6 meses</option>
                    <option value="12">12 meses</option>
                    <option value="18">18 meses</option>
                    <option value="24">24 meses</option>
                    <option value="36">36 meses</option>
                    <option value="48">48 meses</option>
                    <option value="60">60 meses</option>
                  </Field>
                  <ErrorMessage
                    name="credito.plazoMeses"
                    component="div"
                    className="text-danger small mt-1"
                  />
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label>
                    Garantía Ofrecida <span className="text-danger">*</span>
                  </Label>
                  <Field as={Input} type="select" name="credito.garantia">
                    <option value="">Seleccione...</option>
                    <option value="PRENDA_GRANOS">Prenda sobre Granos</option>
                    <option value="PRENDA_HACIENDA">
                      Prenda sobre Hacienda
                    </option>
                    <option value="HIPOTECA">Hipoteca</option>
                    <option value="WARRANT">Warrant</option>
                    <option value="SGR">Garantía SGR</option>
                    <option value="PERSONAL">Garantía Personal</option>
                  </Field>
                  <ErrorMessage
                    name="credito.garantia"
                    component="div"
                    className="text-danger small mt-1"
                  />
                </FormGroup>
              </Col>
            </Row>

            {/* Resumen de TODOS los datos */}
            <Card className="mt-4 border bg-light">
              <CardHeader className="bg-secondary text-white">
                <h6 className="mb-0">
                  <i className="mdi mdi-clipboard-check me-2" />
                  Resumen de la Solicitud
                </h6>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md={6}>
                    <h6 className="text-muted">Productor</h6>
                    <Table size="sm" borderless className="mb-3">
                      <tbody>
                        <tr>
                          <td>CUIT:</td>
                          <td className="fw-bold">{formData.cuit || "-"}</td>
                        </tr>
                        <tr>
                          <td>Razón Social:</td>
                          <td className="fw-bold">
                            {formData.razonSocial || "-"}
                          </td>
                        </tr>
                        <tr>
                          <td>Email:</td>
                          <td>{formData.email || "-"}</td>
                        </tr>
                        <tr>
                          <td>Teléfono:</td>
                          <td>{formData.telefono || "-"}</td>
                        </tr>
                      </tbody>
                    </Table>

                    <h6 className="text-muted">Establecimientos</h6>
                    {formData.establecimientos?.map((est, i) => (
                      <div key={i} className="small mb-1">
                        <strong>
                          {est.nombre || `Establecimiento ${i + 1}`}
                        </strong>
                        {est.superficieTotal && ` - ${est.superficieTotal} ha`}
                        {est.provincia && ` (${est.provincia})`}
                      </div>
                    ))}
                  </Col>

                  <Col md={6}>
                    <h6 className="text-muted">Crédito Solicitado</h6>
                    <Table size="sm" borderless>
                      <tbody>
                        <tr>
                          <td>Monto:</td>
                          <td className="fw-bold text-success">
                            {values.credito?.monto
                              ? new Intl.NumberFormat("es-AR", {
                                  style: "currency",
                                  currency: "ARS",
                                }).format(values.credito.monto)
                              : "-"}
                          </td>
                        </tr>
                        <tr>
                          <td>Destino:</td>
                          <td>
                            {values.credito?.destino?.replace(/_/g, " ") || "-"}
                          </td>
                        </tr>
                        <tr>
                          <td>Plazo:</td>
                          <td>
                            {values.credito?.plazoMeses
                              ? `${values.credito.plazoMeses} meses`
                              : "-"}
                          </td>
                        </tr>
                        <tr>
                          <td>Garantía:</td>
                          <td>
                            {values.credito?.garantia?.replace(/_/g, " ") ||
                              "-"}
                          </td>
                        </tr>
                      </tbody>
                    </Table>

                    {formData.cultivos?.length > 0 && (
                      <>
                        <h6 className="text-muted mt-3">Cultivos</h6>
                        {formData.cultivos.map((c, i) => (
                          <div key={i} className="small">
                            {c.tipoCultivo} - {c.hectareas} ha
                          </div>
                        ))}
                      </>
                    )}
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </>
        )

      default:
        return null
    }
  }

  // =====================================================
  // RENDER PRINCIPAL
  // =====================================================

  const MainContent = () => (
    <Row>
      <Col xs={12}>
        <Card className="shadow">
          <CardHeader className="bg-white border-bottom">
            <h5 className="mb-0 text-dark">
              <i className="mdi mdi-file-document-edit me-2 text-primary" />
              Nueva Solicitud de Crédito Agropecuario
            </h5>
          </CardHeader>
          <CardBody>
            {/* Barra de progreso */}
            <Progress
              value={pasoActual * 25}
              color="success"
              className="mb-3"
              style={{ height: "8px" }}
            />

            {/* Navegación de pasos */}
            <Nav pills className="justify-content-center mb-4">
              {[
                { num: 1, label: "Productor", icon: "mdi-account" },
                { num: 2, label: "Establecimientos", icon: "mdi-barn" },
                { num: 3, label: "Actividad", icon: "mdi-sprout" },
                { num: 4, label: "Crédito", icon: "mdi-cash" },
              ].map(({ num, label, icon }) => (
                <NavItem key={num}>
                  <NavLink
                    className={classnames({
                      "active bg-success": pasoActual === num,
                      "text-success": pasoActual > num,
                      "text-muted": pasoActual < num,
                    })}
                    style={{
                      cursor: pasoActual >= num ? "pointer" : "default",
                    }}
                    onClick={() => pasoActual >= num && setPasoActual(num)}
                  >
                    <Badge
                      color={
                        pasoActual > num
                          ? "success"
                          : pasoActual === num
                            ? "light"
                            : "secondary"
                      }
                      className={`me-2 ${pasoActual === num ? "text-success" : ""}`}
                    >
                      {pasoActual > num ? <i className="mdi mdi-check" /> : num}
                    </Badge>
                    <i className={`mdi ${icon} me-1 d-none d-md-inline`} />
                    {label}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>

            {/* FORMIK */}
            <Formik
              initialValues={formData}
              validationSchema={getValidationSchema(pasoActual)}
              onSubmit={handleSubmit}
              enableReinitialize={true}
              validateOnChange={false}
              validateOnBlur={true}
            >
              {({ values, setFieldValue, validateForm, setTouched }) => (
                <Form>
                  {/* Contenido del paso actual */}
                  {renderPaso(values, setFieldValue)}

                  {/* Botones de navegación */}
                  <hr className="my-4" />
                  <div className="d-flex justify-content-between">
                    <Button
                      color="secondary"
                      outline
                      onClick={() => handleAnterior(values)}
                      disabled={pasoActual === 1}
                    >
                      <i className="mdi mdi-arrow-left me-1" />
                      Anterior
                    </Button>

                    {pasoActual < 4 ? (
                      <Button
                        color="primary"
                        onClick={() =>
                          handleSiguiente(values, validateForm, setTouched)
                        }
                      >
                        Siguiente
                        <i className="mdi mdi-arrow-right ms-1" />
                      </Button>
                    ) : (
                      <Button
                        color="success"
                        type="submit"
                        disabled={submitting}
                        size="lg"
                      >
                        {submitting ? (
                          <>
                            <Spinner size="sm" className="me-2" /> Enviando...
                          </>
                        ) : (
                          <>
                            <i className="mdi mdi-check-bold me-1" /> Crear
                            Solicitud
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </Form>
              )}
            </Formik>
          </CardBody>
        </Card>
      </Col>
    </Row>
  )

  return (
    <Layout
      components={[{ component: <MainContent />, size: { xs: 12 } }]}
      title="Nueva Solicitud de Crédito"
    />
  )
}

export default FormularioCarga
